/** Pure rules of the "ลงออเดอร์" form (no Vue): request bodies, what blocks saving, texts. */
import type {
  ChatOrderRef,
  PosCartBody,
  PosCatalog,
  PosDelivery,
  PosCartItem,
  PosHistoryOrder,
  PosOrderBody,
  PosOrderResult,
  PosPayMethod,
  PosQuote,
  PosShipping,
} from '../api/pos/types'
import { dayKey } from './inbox'

export type CouponState =
  | { state: 'none' }
  | { state: 'checking'; code: string }
  /** Discount frozen at code entry for `setMenuId` (POS sale-page rule). */
  | { state: 'ok'; code: string; token: string; discount: number; freeItems: number; setMenuId: number | null; label: string }
  /** The pack changed after the code was entered: the POS needs a new check. */
  | { state: 'stale'; code: string; discount: number }
  | { state: 'error'; code: string; message: string }

export interface OrderForm {
  customerId: string | null
  /** New pack, or continue an open pack of this customer. */
  setMenuId: number | null
  continueHeaderId: number | null
  /** Pick order matters: boxes over the quota are counted from the end of this list (POS rule). */
  items: PosCartItem[]
  shipping: PosShipping
  /** YYYY-MM-DD (Bangkok). */
  date: string
  /** Still the default date (follows catalog.delivery.default_date until the staff picks a day). */
  dateAuto: boolean
  /** Slot time ("11:00"); DHL uses the first slot like the POS. */
  time: string | null
  remark: string
  couponCode: string
  coupon: CouponState
  feeOverride: { on: boolean; fee: string; reason: string }
  /** `from`: where the date / time / amount came from — the customer's checked slip, or the staff's own typing (the
   *  slip never overwrites what the staff typed). */
  pay: { method: PosPayMethod | null; date: string; time: string; amount: string; from?: 'slip' | 'staff' }
  /** How the cart was started ("สั่งซ้ำจาก …", "สั่งต่อ …", "เติมจากแชท …"), shown above the form with its notes. */
  source: { kind: 'repeat' | 'continue' | 'chat'; label: string; notes?: string[] } | null
}

const DAY = 86400000
export const OVERRIDE_REASON_MIN = 3
export const LOCKED_TEXT = 'วันส่งนี้ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว) เลือกวันอื่น'
/** A Lalamove order of a POS customer without a pin (owner 18 ต.ค. 69 "ถ้า lalamove ยังไงต้องมีหมุด"): the manual fee no
 *  longer stands in for it — the pin is set in section 1 (CustomerBlock), or the order goes DHL. */
export const PIN_REQUIRED_TEXT = 'ลูกค้ายังไม่มีหมุด ส่ง Lalamove ต้องมีหมุด ใส่หมุดที่ส่วนลูกค้าใน POS ด้านบนก่อนบันทึก'

export function tomorrow(nowMs: number): string {
  return dayKey(nowMs + DAY)
}

const addDays = (ymd: string, n: number): string => dayKey(Date.parse(`${ymd}T12:00:00+07:00`) + n * DAY)
const weekday = (ymd: string): number => new Date(Date.parse(`${ymd}T12:00:00+07:00`)).getUTCDay()

/** POS sale page default (ksNextDay('Saturday')): tomorrow, or the day after when tomorrow is a Saturday. */
export function defaultDate(nowMs: number): string {
  const t = tomorrow(nowMs)
  return weekday(t) === 6 ? addDays(t, 1) : t
}

const FALLBACK_SLOTS = [
  { time: '11:00', label: '10:00 - 12:00' },
  { time: '14:00', label: '13:00 - 15:00' },
  { time: '16:00', label: '15:00 - 17:00' },
  { time: '18:00', label: '17:00 - 19:00' },
]

/** catalog.delivery, or the POS sale-page defaults when the catalog (or an old API) has none. */
export function deliveryOf(catalog: PosCatalog | null, nowMs: number = Date.now()): PosDelivery {
  if (catalog?.delivery?.slots?.length) return catalog.delivery
  const today = dayKey(nowMs)
  const slots = catalog?.delivery_slots?.length ? catalog.delivery_slots : FALLBACK_SLOTS
  return {
    today,
    server_time: '',
    default_date: defaultDate(nowMs),
    default_time: slots[0]?.time ?? '11:00',
    date_min: today,
    date_max: addDays(today, 90),
    slots,
    locked_dates: [],
    advisory: null,
    source: 'fallback',
  }
}

/** A Lalamove order cannot be saved on this date (409 dayLocked). DHL is not affected. */
export function isLocked(d: PosDelivery, date: string, shipping: PosShipping): boolean {
  return shipping === 'lalamove' && d.locked_dates.some((l) => l.date === date && (l.carrier ?? 'lalamove') === 'lalamove')
}

const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const DAY_TH: Record<string, string> = { sun: 'อาทิตย์', mon: 'จันทร์', tue: 'อังคาร', wed: 'พุธ', thu: 'พฤหัสบดี', fri: 'ศุกร์', sat: 'เสาร์' }
const CARRIER_TH: Record<string, string> = { lalamove: 'Lalamove', dhl: 'DHL' }

/** Why the shop does not deliver on a day (weekly day off of the carrier, kitchen holiday, web closed) — such a day
 *  cannot be picked (owner 18 ต.ค. 69 "ถ้าเป็นวันหยุดไม่สามารถเลือกได้"): the chip is disabled, saving refuses it. */
export function dayNotes(d: PosDelivery, date: string, shipping: PosShipping): string[] {
  const a = d.advisory
  if (!a) return []
  const out: string[] = []
  const day = DAY_NAMES[weekday(date)] ?? ''
  if (a.weekoff?.[shipping]?.includes(day)) out.push(`เว็บปกติไม่ส่ง ${CARRIER_TH[shipping]} วัน${DAY_TH[day]}`)
  for (const h of a.holidays ?? []) {
    if (h.date === date && (!h.carrier || h.carrier === shipping || h.carrier === 'all')) out.push(h.name ? `วันหยุด: ${h.name}` : 'วันหยุดครัว')
  }
  for (const c of a.closed_days ?? []) if (c.date === date) out.push(c.note ? `เว็บปิดรับ: ${c.note}` : 'เว็บปิดรับวันนี้')
  return out
}

/** A day off for this carrier (dayNotes has a reason). */
export function isDayOff(d: PosDelivery, date: string, shipping: PosShipping): boolean {
  return dayNotes(d, date, shipping).length > 0
}

/** `from`, or the first later day the carrier delivers (not locked, not a day off) up to `date_max` — else `from`. */
export function firstOpenDay(d: PosDelivery, from: string, shipping: PosShipping): string {
  for (let date = from; date <= d.date_max; date = addDays(date, 1)) {
    if (!isLocked(d, date, shipping) && !isDayOff(d, date, shipping)) return date
  }
  return from
}

export interface DateChip {
  date: string
  /** "วันนี้" · "พรุ่งนี้" · "จ." */
  top: string
  /** Day of month. */
  day: string
  locked: boolean
  /** A day off (holiday / weekly day off / web closed): cannot be picked. */
  off: boolean
  notes: string[]
}

const fmtWeekday = new Intl.DateTimeFormat('th-TH', { weekday: 'short', timeZone: 'Asia/Bangkok' })

/** The next `n` days from date_min for the quick date picker (locked days come with `locked`). */
export function dateChips(d: PosDelivery, shipping: PosShipping, n = 14): DateChip[] {
  const out: DateChip[] = []
  for (let i = 0; i < n; i++) {
    const date = addDays(d.date_min, i)
    if (date > d.date_max) break
    const top = date === d.today ? 'วันนี้' : date === addDays(d.today, 1) ? 'พรุ่งนี้' : fmtWeekday.format(new Date(Date.parse(`${date}T12:00:00+07:00`)))
    const notes = dayNotes(d, date, shipping)
    out.push({ date, top, day: String(Number(date.slice(8, 10))), locked: isLocked(d, date, shipping), off: notes.length > 0, notes })
  }
  return out
}

/** The payment the customer's newest checked slip shows (owner 18 ต.ค. 69 "วันและเวลาอ้างอิงจากสลิปที่โอน"): a slip that
 *  passed (or needs a look) with its transfer time — never a duplicate / wrong receiver / not a slip. */
export function slipPayment(
  items: { kind: string; id?: string; direction?: string; slip?: { status: string; slip_at: string | null; amount: number | null; bank: string | null; label: string } | null }[],
): { date: string; time: string; amount: string; bank: string | null; label: string; messageId: string | null; status: string; slip_at: string; amountNum: number | null } | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const m = items[i]!
    const s = m.kind === 'message' && m.direction === 'in' ? m.slip : null
    if (!s || !s.slip_at || !['ok', 'review'].includes(s.status)) continue
    const ms = Date.parse(s.slip_at)
    if (Number.isNaN(ms)) continue
    return {
      date: dayKey(ms), time: bangkokTime(ms), amount: s.amount != null ? String(s.amount) : '', bank: s.bank, label: s.label,
      // "ยืนยันชำระ" of a held order records which slip it was (one slip confirms one order)
      messageId: m.id ?? null, status: s.status, slip_at: s.slip_at, amountNum: s.amount,
    }
  }
  return null
}

/** A Lalamove slot: `time` is what the POS stores, `label` what staff read ("10:00 - 12:00"). */
export interface PosSlotLike {
  time: string
  label: string
}

export function bangkokTime(nowMs: number): string {
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' })
    .format(new Date(nowMs))
    .replace(/^24:/, '00:')
}

export function emptyForm(nowMs: number, customerId: string | null = null, catalog: PosCatalog | null = null): OrderForm {
  return {
    customerId,
    setMenuId: null,
    continueHeaderId: null,
    items: [],
    shipping: 'lalamove',
    date: deliveryOf(catalog, nowMs).default_date,
    dateAuto: true,
    time: null,
    remark: '',
    couponCode: '',
    coupon: { state: 'none' },
    feeOverride: { on: false, fee: '', reason: '' },
    pay: { method: null, date: dayKey(nowMs), time: bangkokTime(nowMs), amount: '' },
    source: null,
  }
}

/**
 * "แก้ไขออเดอร์" of a held order (owner 18 ต.ค. 69 "พักบิลสามารถแก้ไขออเดอร์ได้"): the form prefilled with what the held bill
 * was saved / last edited with (`hold.cart` from the server) — the same customer, the payment stays "ยังไม่โอน". The coupon
 * keeps its token (the discount frozen at entry; a pack change makes it stale like any form). null = nothing to prefill.
 */
export function heldForm(o: ChatOrderRef, nowMs: number, catalog: PosCatalog | null): OrderForm | null {
  // a card link edits the same way (its cart, the payment stays "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า")
  const c = o.hold?.cart ?? o.card?.cart
  if (!c) return null
  const f = emptyForm(nowMs, o.customer_id, catalog)
  f.setMenuId = c.set_menu_id
  f.items = c.items.map((i) => ({ menu_id: i.menu_id, qty: i.qty }))
  f.shipping = c.shipping
  f.date = c.delivery_date
  f.dateAuto = false
  f.time = c.shipping === 'dhl' ? null : c.delivery_time
  f.remark = c.remark
  if (c.coupon) {
    f.couponCode = c.coupon.code
    // no token (an older save): the staff press "ใช้โค้ด" again
    if (c.coupon.token) {
      const label = c.coupon.free_items ? `ฟรี ${c.coupon.free_items} กล่อง` : 'ส่วนลดเดิมของพักบิล'
      f.coupon = { state: 'ok', code: c.coupon.code, token: c.coupon.token, discount: c.coupon.discount,
        freeItems: c.coupon.free_items, setMenuId: c.set_menu_id, label }
    }
  }
  if (c.shipping_fee !== null) f.feeOverride = { on: true, fee: String(c.shipping_fee), reason: c.override_reason ?? '' }
  f.pay = { ...f.pay, method: o.card ? 'CardLink' : 'PendingTransfer' }
  return f
}

export function menuOf(catalog: PosCatalog | null, id: number) {
  return catalog?.menus.find((m) => m.id === id) ?? null
}

/** Meals (count towards the pack) and snacks picked so far. */
export function counts(form: OrderForm, catalog: PosCatalog | null): { meals: number; snacks: number } {
  let meals = 0
  let snacks = 0
  for (const it of form.items) {
    if (menuOf(catalog, it.menu_id)?.type === 'snacks') snacks += it.qty
    else meals += it.qty
  }
  return { meals, snacks }
}

export function qtyOf(form: OrderForm, menuId: number): number {
  return form.items.find((i) => i.menu_id === menuId)?.qty ?? 0
}

/** Set a menu's quantity; a new menu goes to the end (pick order), 0 removes it. */
export function withQty(items: PosCartItem[], menuId: number, qty: number): PosCartItem[] {
  const q = Math.max(0, Math.min(500, Math.floor(qty)))
  const i = items.findIndex((x) => x.menu_id === menuId)
  if (q === 0) return i < 0 ? items : items.filter((x) => x.menu_id !== menuId)
  if (i < 0) return [...items, { menu_id: menuId, qty: q }]
  return items.map((x) => (x.menu_id === menuId ? { ...x, qty: q } : x))
}

export function feeOverrideValue(form: OrderForm): number | null {
  if (!form.feeOverride.on) return null
  const t = form.feeOverride.fee.trim()
  if (!/^\d{1,4}$/.test(t)) return null
  const n = Number(t)
  return n <= 2000 ? n : null
}

/** Lalamove: the picked round. DHL: none (the POS uses delivery.default_time). */
function slotTime(form: OrderForm): string | null {
  return form.shipping === 'dhl' ? null : form.time
}

/** Why the live price cannot be computed yet (empty = ready). */
export function quoteMissing(form: OrderForm): string[] {
  const out: string[] = []
  if (!form.customerId) out.push('เลือกลูกค้าใน POS')
  if (!form.setMenuId && !form.continueHeaderId) out.push('เลือกแพ็ก')
  if (!form.items.length) out.push('เลือกเมนูอย่างน้อย 1 กล่อง')
  return out
}

/** The cart as sent for a quote, or null while `quoteMissing` is not empty. */
export function cartBody(conversationId: string, form: OrderForm, catalog: PosCatalog | null): PosCartBody | null {
  if (quoteMissing(form).length) return null
  const body: PosCartBody = {
    conversation_id: conversationId,
    customer_id: Number(form.customerId),
    items: form.items.map((i) => ({ menu_id: i.menu_id, qty: i.qty })),
    shipping: form.shipping,
    delivery_date: form.date,
    remark: form.remark,
  }
  // the fee does not depend on the round: quote with the default round until one is picked
  if (form.shipping === 'lalamove') body.delivery_time = slotTime(form) ?? deliveryOf(catalog).default_time
  if (form.continueHeaderId) body.continue_header_id = form.continueHeaderId
  else body.set_menu_id = form.setMenuId
  if (form.coupon.state === 'ok' && form.coupon.setMenuId === (form.setMenuId ?? null) && !form.continueHeaderId) {
    body.coupon = { code: form.coupon.code, token: form.coupon.token }
  }
  const fee = feeOverrideValue(form)
  if (fee !== null) {
    body.shipping_fee = fee
    body.override_reason = form.feeOverride.reason.trim()
  }
  if (form.pay.method === 'CashOnDelivery') body.payment = { method: 'CashOnDelivery' }
  return body
}

/** Everything that blocks "บันทึก" (empty = can save). The first one is shown under the button. `hasPin`: the POS
 *  customer's pin (false = none: a Lalamove order cannot be saved · null = not known, the POS still refuses it). */
export function saveIssues(
  form: OrderForm,
  quote: { state: 'idle' | 'loading' | 'ok' | 'error'; data: PosQuote | null; error: string | null; fresh: boolean },
  blockers: string[] = [],
  delivery: PosDelivery | null = null,
  hasPin: boolean | null = null,
  nowMs: number | null = null,
): string[] {
  const out = [...blockers, ...quoteMissing(form)]
  const needPin = form.shipping === 'lalamove' && !!form.customerId && hasPin === false
  if (needPin) out.push(PIN_REQUIRED_TEXT)
  if (delivery && (form.date < delivery.date_min || form.date > delivery.date_max)) {
    out.push(`วันส่งต้องอยู่ระหว่าง ${deliveryDateLabel(delivery.date_min)} ถึง ${deliveryDateLabel(delivery.date_max)}`)
  }
  if (delivery && isLocked(delivery, form.date, form.shipping)) out.push(LOCKED_TEXT)
  else if (delivery && isDayOff(delivery, form.date, form.shipping)) {
    out.push(`วันส่งตรงวันหยุด (${dayNotes(delivery, form.date, form.shipping).join(' · ')}) เลือกวันอื่น`)
  }
  if (form.shipping === 'lalamove' && !form.time) out.push('เลือกรอบส่ง')
  if (form.feeOverride.on && feeOverrideValue(form) === null) out.push('ใส่ค่าส่งที่แก้ (0–2,000 บาท)')
  if (form.feeOverride.on && form.feeOverride.reason.trim().length < OVERRIDE_REASON_MIN) {
    out.push('ใส่เหตุผลที่แก้ค่าส่ง')
  }
  const c = form.coupon
  if (c.state === 'checking') out.push('รอตรวจโค้ดส่วนลด')
  if (c.state === 'stale') out.push('เปลี่ยนแพ็กแล้ว ตรวจโค้ดส่วนลดอีกครั้ง')
  if (c.state === 'error') out.push('โค้ดส่วนลดใช้ไม่ได้ ลบออกก่อนบันทึก')
  if (c.state === 'none' && form.couponCode.trim()) out.push('กดใช้โค้ดส่วนลด หรือลบออก')
  if (!form.pay.method) out.push('เลือกวิธีชำระเงิน')
  if (form.pay.method === 'BankTransfer' && (!/^\d{4}-\d{2}-\d{2}$/.test(form.pay.date) || !/^\d{2}:\d{2}$/.test(form.pay.time))) {
    out.push('ใส่วันและเวลาที่ลูกค้าโอน')
  }
  if (form.pay.method === 'BankTransfer' && form.pay.amount.trim() && !(Number(form.pay.amount) > 0)) {
    out.push('ยอดโอนไม่ถูกต้อง')
  }
  // the POS sale page's held bill keeps a new pack only (opening it there would make a new pack)
  if (form.pay.method === 'PendingTransfer' && form.continueHeaderId) out.push(PENDING_CONTINUE_TEXT)
  if (form.pay.method === 'PendingTransfer' && quote.state === 'ok' && quote.fresh && quote.data && quote.data.totals.total <= 0) {
    out.push('ยอดเป็น 0 ไม่ต้องรอโอน เลือก "ลูกค้าโอนแล้ว" แทน')
  }
  // "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า": the POS sale page has no card link for a continued pack, and a link for a day whose
  // 21:30 the day before already passed would be born expired (the POS refuses it: cardLinkTooLate)
  if (form.pay.method === 'CardLink' && form.continueHeaderId) out.push(CARD_CONTINUE_TEXT)
  if (form.pay.method === 'CardLink' && nowMs !== null && cardTooLate(form.date, nowMs)) out.push(CARD_TOO_LATE_TEXT)
  if (form.pay.method === 'CardLink' && quote.state === 'ok' && quote.fresh && quote.data && quote.data.totals.total <= 0) {
    out.push('ยอดเป็น 0 ไม่ต้องมีลิงก์ชำระ เลือก "ลูกค้าโอนแล้ว" แทน')
  }
  if (!quoteMissing(form).length) {
    if (quote.state === 'error') out.push(quote.error ?? 'คำนวณยอดไม่สำเร็จ')
    else if (quote.state !== 'ok' || !quote.fresh) out.push('รอคำนวณยอด')
    // the manual fee with a reason: a pin the system cannot price (outside the Lalamove zone, a closed area) — not a
    // missing pin (PIN_REQUIRED_TEXT above)
    else if (!needPin && quote.data && quote.data.shipping.fee_system === null && feeOverrideValue(form) === null) {
      out.push('ระบบคิดค่าส่งไม่ได้ ใส่ค่าส่งเองพร้อมเหตุผล')
    }
  }
  return [...new Set(out)]
}

export const PENDING_CONTINUE_TEXT = 'สั่งต่อแพ็กเดิมพักบิลรอโอนไม่ได้ ให้ลูกค้าโอนก่อนแล้วเลือก "ลูกค้าโอนแล้ว"'
export const CARD_CONTINUE_TEXT = 'สั่งต่อแพ็กเดิมสร้างลิงก์ชำระบัตรไม่ได้ (หน้าขาย POS ก็ไม่มี) ให้ลูกค้าโอนก่อนแล้วเลือก "ลูกค้าโอนแล้ว"'
export const CARD_TOO_LATE_TEXT =
  'ลิงก์บัตรของวันส่งนี้หมดเวลาแล้ว (ชำระได้ถึง 21:30 ของวันก่อนส่ง) เลือกวันส่งใหม่ หรือใช้วิธีชำระอื่น'

/** When a card link for this delivery day expires: 21:30 Bangkok the day before (POS sale-page rule — owner 18 ต.ค. 69). */
export function cardExpiry(ymd: string): number {
  return Date.parse(`${ymd}T21:30:00+07:00`) - DAY
}

/** A card link for this day would be born expired (the chat itself takes orders until 22:00 — 21:30–22:00 = no link). */
export function cardTooLate(ymd: string, nowMs: number): boolean {
  const t = cardExpiry(ymd)
  return Number.isNaN(t) || nowMs >= t
}

export function orderBody(conversationId: string, form: OrderForm, catalog: PosCatalog | null): Omit<PosOrderBody, 'client_id'> | null {
  const cart = cartBody(conversationId, form, catalog)
  if (!cart || !form.pay.method) return null
  const time = slotTime(form)
  if (form.shipping === 'lalamove' && !time) return null
  const amount = form.pay.amount.trim() ? Number(form.pay.amount) : null
  const { delivery_time: _quoteTime, ...rest } = cart
  return {
    ...rest,
    ...(time ? { delivery_time: time } : {}),
    payment:
      form.pay.method === 'BankTransfer'
        ? { method: 'BankTransfer', paid_at: `${form.pay.date} ${form.pay.time}`, amount }
        : form.pay.method === 'PendingTransfer' || form.pay.method === 'CardLink'
          ? { method: form.pay.method }
          : { method: 'CashOnDelivery' },
  }
}

/** Stable text of a request (key order fixed) — the same click is re-sent only if nothing changed. */
export function fingerprint(body: unknown): string {
  const norm = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(norm)
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.keys(v as Record<string, unknown>)
          .sort()
          .map((k) => [k, norm((v as Record<string, unknown>)[k])]),
      )
    }
    return v
  }
  return JSON.stringify(norm(body))
}

const fmtDelivery = new Intl.DateTimeFormat('th-TH', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' })
const fmtMoney = new Intl.NumberFormat('th-TH')

/** "อา. 28 ก.ย." from YYYY-MM-DD. */
export function deliveryDateLabel(ymd: string): string {
  const t = Date.parse(`${ymd}T12:00:00+07:00`)
  return Number.isNaN(t) ? ymd : fmtDelivery.format(new Date(t))
}

export function slotLabel(catalog: PosCatalog | null, time: string | null): string | null {
  if (!time) return null
  return deliveryOf(catalog).slots.find((s) => s.time === time)?.label ?? time
}

/** Text for the customer (goes into the composer; staff check it and press send). Polite wording. */
export function billText(res: PosOrderResult, catalog: PosCatalog | null): string {
  const o = res.order.order
  const code = res.chat_order.order_code ?? res.order.order_code ?? ''
  const lines = [`สรุปคำสั่งซื้อ ${code}`]
  if (o) {
    const pack = o.continue ? `${o.set_menu.name} (สั่งต่อ)` : o.set_menu.name
    lines.push(`${pack} · ${o.meals} มื้อ`)
    const when = deliveryDateLabel(o.delivery.date)
    lines.push(
      o.shipping.carrier === 'dhl'
        ? `จัดส่ง DHL รอบ ${when}`
        : `จัดส่ง ${when} ช่วง ${slotLabel(catalog, o.delivery.time) ?? o.delivery.time} น.`,
    )
    const cod = o.totals.cod_fee > 0
    lines.push(`ยอดรวม ${fmtMoney.format(o.totals.total)} บาท${cod ? ' (เก็บเงินปลายทาง)' : ' (ชำระแล้ว)'}`)
  }
  const url = res.chat_order.bill_url ?? res.order.bill_url
  // the link on a line of its own (owner 18 ต.ค. 69 "เว้นบรรทัด url"): LINE / Messenger make it tappable whole
  if (url) lines.push('ดูรายละเอียดคำสั่งซื้อได้ที่', url)
  return lines.join('\n')
}

/**
 * "ส่งใบสรุปยอดและ QR ในแชท" of an order saved "ยังไม่โอน" (the PromptPay QR goes as a picture after this text). No bill
 * link: the POS makes the order (and its bill) when the payment is confirmed — "ส่งใบสรุปยอดในแชท" sends it then.
 * Customer wording per the tone guide (an invitation, คุณลูกค้า/แอดมิน, ค่ะ/คะ, no emoji).
 */
/** "สรุปคำสั่งซื้อ" + pack · delivery · the amount to pay, of an order that waits for its payment (QR or card link). */
function payingSummary(res: PosOrderResult, catalog: PosCatalog | null): string[] {
  const o = res.order.order
  const lines = ['สรุปคำสั่งซื้อ']
  const total = o?.totals.total ?? res.chat_order.total
  if (o) {
    const pack = o.continue ? `${o.set_menu.name} (สั่งต่อ)` : o.set_menu.name
    lines.push(`${pack} · ${o.meals} มื้อ`)
    const when = deliveryDateLabel(o.delivery.date)
    lines.push(
      o.shipping.carrier === 'dhl'
        ? `จัดส่ง DHL รอบ ${when}`
        : `จัดส่ง ${when} ช่วง ${slotLabel(catalog, o.delivery.time) ?? o.delivery.time} น.`,
    )
  }
  if (total !== null && total !== undefined) lines.push(`ยอดที่ต้องชำระ ${fmtMoney.format(total)} บาท`)
  return lines
}

export function pendingBillText(res: PosOrderResult, catalog: PosCatalog | null, paymentText = ''): string {
  const lines = payingSummary(res, catalog)
  // one idea per paragraph (blank line between), like the other customer texts
  const pay = paymentText.trim()
  if (!pay) return `${lines.join('\n')}\n\nสะดวกโอนผ่าน QR พร้อมเพย์ด้านล่างได้เลยนะคะ โอนแล้วส่งสลิปให้แอดมินในแชทนี้ได้เลยค่ะ`
  // owner 18 ต.ค. 69 "เพิ่มเลขบัญชีธนาคารด้วยและบอกว่าคุณลูกค้าสามารถเลือกได้" — the account is the owner's own
  // "ข้อความแจ้งการชำระเงิน" (settings), word for word
  // the owner's text already asks for the slip: not asked twice
  return [
    lines.join('\n'),
    `คุณลูกค้าสามารถเลือกชำระได้ 2 ช่องทางตามสะดวกค่ะ\n1. สแกน QR พร้อมเพย์ในรูปด้านล่าง\n2. โอนเข้าบัญชีธนาคาร\n${pay}`,
    ...(pay.includes('สลิป') ? [] : ['โอนแล้วส่งสลิปให้แอดมินในแชทนี้ได้เลยค่ะ']),
  ].join('\n\n')
}

/**
 * "ส่งลิงก์ให้ลูกค้า" of a card link (owner 18 ต.ค. 69): the order summary, the link on a line of its own (LINE / Messenger make
 * it tappable), until when it can be paid, and that the order is confirmed by itself once paid. Polite, one idea per
 * paragraph, no emoji, no contact or bank details (the link is the only way to pay). Goes into the composer — staff send it.
 */
export function cardLinkText(res: PosOrderResult, catalog: PosCatalog | null): string {
  const card = res.chat_order.card
  const out = [payingSummary(res, catalog).join('\n')]
  if (card?.link) out.push(`คุณลูกค้าสามารถชำระด้วยบัตรเครดิตได้ที่ลิงก์นี้ค่ะ\n${card.link}`)
  const exp = card?.expire_at ? Date.parse(card.expire_at) : NaN
  if (!Number.isNaN(exp)) out.push(`ลิงก์ชำระได้ถึง ${deliveryDateLabel(dayKey(exp))} ${bangkokTime(exp)} น.`)
  out.push('ชำระแล้วระบบยืนยันคำสั่งซื้อให้อัตโนมัติค่ะ')
  return out.join('\n\n')
}

/** "ลิงก์ชำระได้ถึง ศ. 4 ต.ค. 21:30 น." · expired: nobody can pay it until "ต่ออายุ" (staff only). */
export function cardDueText(card: { expire_at: string | null; expired: boolean } | null | undefined): { text: string; late: boolean } | null {
  if (!card?.expire_at) return null
  const t = Date.parse(card.expire_at)
  if (Number.isNaN(t)) return null
  const at = `${deliveryDateLabel(dayKey(t))} ${bangkokTime(t)} น.`
  return card.expired
    ? { text: `ลิงก์หมดอายุแล้ว (${at}) ลูกค้าจ่ายไม่ได้ กด "ต่ออายุ" แล้วเลือกวันส่งใหม่ หรือยกเลิกลิงก์`, late: true }
    : { text: `ลิงก์ชำระได้ถึง ${at}`, late: false }
}

/** "ควรชำระภายใน ศ. 4 ต.ค. 22:00 น." — the POS's pay_due_at = the chat's order cutoff the day before delivery (owner 18 ต.ค. 69
 *  "ผ่านแชทปิดรับ 22.00", not the website's 21:30) · after it: passed (staff only — nothing happens by itself). */
export function holdDueText(hold: { pay_due_at: string | null; late: boolean } | null | undefined): { text: string; late: boolean } | null {
  if (!hold?.pay_due_at) return null
  const t = Date.parse(hold.pay_due_at)
  if (Number.isNaN(t)) return null
  const at = `${deliveryDateLabel(dayKey(t))} ${bangkokTime(t)} น.`
  return hold.late
    ? { text: `เลยเวลาชำระของรอบนี้แล้ว (${at} · ปิดรับออเดอร์ผ่านแชทของวันก่อนส่ง) ยืนยันชำระได้ แต่เช็กกับครัวว่ายังทันรอบ หรือเลือกวันส่งใหม่`, late: true }
    : { text: `ควรชำระภายใน ${at} (ปิดรับผ่านแชท)`, late: false }
}

/** "ยืนยันชำระ" prefilled from a checked slip: its time and amount, and how far it is from the order total. */
export interface PayPrefill {
  paid_at: string
  amount: number
  /** amount − total (0 = equal). */
  diff: number
  slipMessageId: string | null
}

export function payPrefill(
  slip: { slip_at: string | null; amount: number | null } | null,
  total: number | null,
  slipMessageId: string | null = null,
): PayPrefill | null {
  if (!slip?.slip_at || slip.amount === null || slip.amount === undefined || total === null) return null
  const ms = Date.parse(slip.slip_at)
  if (Number.isNaN(ms)) return null
  return {
    paid_at: `${dayKey(ms)} ${bangkokTime(ms)}`,
    amount: slip.amount,
    diff: Math.round((slip.amount - total) * 100) / 100,
    slipMessageId,
  }
}

/** The held orders a slip may confirm: still held, newest first. */
export function heldOf(orders: ChatOrderRef[] | null | undefined): ChatOrderRef[] {
  return (orders ?? []).filter((o) => o.status === 'held' && !!o.hold)
}

export interface HistoryStats {
  count: number
  total: number
  avg: number
}

/** Over the orders the POS returned (newest first, ≤ 20), cancelled ones excluded. */
export function historyStats(orders: PosHistoryOrder[]): HistoryStats {
  const ok = orders.filter((o) => o.status !== '-1')
  const total = ok.reduce((a, o) => a + o.total, 0)
  return { count: ok.length, total, avg: ok.length ? Math.round(total / ok.length) : 0 }
}

/** Match a past order's pack name to a pack that is still sold (for "สั่งซ้ำ"). */
export function packForName(catalog: PosCatalog | null, name: string): number | null {
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim()
  return catalog?.set_menus.find((s) => norm(s.name) === norm(name))?.id ?? null
}

/** "13.7,100.5" or a Google Maps link (…@13.7,100.5… · ?q=13.7,100.5 · !3d13.7!4d100.5) → pin. */
export function parsePin(text: string): { lat: number; lng: number } | null {
  const t = text.trim()
  if (!t) return null
  const pats = [/@(-?\d+\.\d+),\s*(-?\d+\.\d+)/, /[?&](?:q|query|ll)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/, /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, /^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/]
  for (const p of pats) {
    const m = t.match(p)
    if (m) {
      const lat = Number(m[1])
      const lng = Number(m[2])
      if (lat > 5 && lat < 21 && lng > 97 && lng < 106) return { lat, lng }
      return null
    }
  }
  return null
}

export const newClientId = (): string =>
  `o${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
